import { useFormContext } from "react-hook-form";
import MultiSelectBox from "../../../../gdk/MultiSelectBox";
import FloridaCountyOptions from "../../../constants/options/FloridaCountyOptions";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import { useContext, useEffect, useRef } from "react";
import YesNoOptions from "../../../../../constants/options/YesNoOptions";
import AddressFields from "../../../../AddressFields";
import { AddressLabelsWithRequiredIndicator } from "../../../../../constants/AddressFieldLabels";
import { GeaoContext } from "../../../GeaoContextProvider";
import { ClientConfigurationContext } from "../../../../../context/ClientConfigurationContextProvider";
import { classes } from "../../../../../util/style-utils";

/**
 * Location Information Section Component
 * 
 * A form section that collects residential address information and Florida business
 * location details. The component dynamically shows/hides Florida county selection
 * based on the state selection and business location preferences.
 * 
 * @component
 * @returns {JSX.Element} The rendered location information form section
 * 
 * @example
 * // Usage within a react-hook-form FormProvider context
 * <FormProvider methods={methods}>
 *   <LocationInfoSection />
 * </FormProvider>
 * 
 * @requires react-hook-form - Must be used within a FormProvider context
 * 
 * Form fields managed:
 * - residentialAddress.streetAddressLineOne: String - First line of residential address
 * - residentialAddress.streetAddressLineTwo: String - Second line of residential address
 * - residentialAddress.city: String - Residential city
 * - residentialAddress.state: String - Residential state
 * - residentialAddress.zipCode: String - Residential ZIP code
 * - residentialAddress.county: Array - Florida counties (conditional, only when state is FL)
 * - businessInFlorida: String - Yes/No whether conducting business in Florida
 * - businessInFloridaCounty: Array - Florida counties for business (conditional)
 * 
 * Conditional behavior:
 * - County selection appears only when residential state is Florida
 * - Business county selection appears only when businessInFlorida is "true"
 * - Form values are automatically cleared when conditions are no longer met
 */
export default function LocationInfoSection() {
    const {
        watch,
        control,
        register,
        setValue,
        formState: { errors }
    } = useFormContext();

    const { agentInterview } = useContext(GeaoContext);
    const clientConfiguration = useContext(ClientConfigurationContext);

    const isOffshoreResident = agentInterview?.questionAnswers?.OffshoreResident?.answer === "true";
    const enableOffshore = clientConfiguration?.enableOffshore === true;
    const showCountryField = enableOffshore && isOffshoreResident;

    const watchResidentialAddressState = watch("residentialAddress.state");
    const watchResidentialAddressCountry = watch("residentialAddress.country");
    const watchBusinessInFlorida = watch("businessInFlorida");

    // For offshore residents, don't default to US - require explicit country selection
    // For non-offshore, default to US
    const selectedCountry = showCountryField
        ? (watchResidentialAddressCountry || "")
        : (watchResidentialAddressCountry || "US");
    const allCountryOptions = clientConfiguration?.countries || [];
    // Filter out USA for offshore residents since they indicated they reside outside of USA
    const countryOptions = showCountryField
        ? allCountryOptions.filter(option => option.value !== "US")
        : allCountryOptions;

    /**
     * Effect to clear residential county selection when state is not Florida
     * Automatically resets the county field to an empty array when the user
     * changes their residential state from Florida to another state
     */
    useEffect(() => {
        if (watchResidentialAddressState !== "FL") {
            setValue("residentialAddress.county", []);
        }
    }, [watchResidentialAddressState, setValue]);

    /**
     * Effect to clear business county selection when not conducting business in Florida
     * Automatically resets the business county field to an empty array when the user
     * indicates they will not be conducting business in Florida
     */
    useEffect(() => {
        if (watchBusinessInFlorida !== "true") {
            setValue("businessInFloridaCounty", []);
        }
    }, [watchBusinessInFlorida, setValue]);

    const prevCountryRef = useRef(watchResidentialAddressCountry);

    /**
     * Effect to reset address when country changes (since state lists differ by country)
     */
    useEffect(() => {
        if (showCountryField && prevCountryRef.current !== watchResidentialAddressCountry) {
            setValue("residentialAddress.streetAddressLineOne", "");
            setValue("residentialAddress.streetAddressLineTwo", "");
            setValue("residentialAddress.city", "");
            setValue("residentialAddress.state", "");
            setValue("residentialAddress.zipCode", "");
            setValue("residentialAddress.county", []);
        }
        prevCountryRef.current = watchResidentialAddressCountry;
    }, [watchResidentialAddressCountry, showCountryField, setValue]);

    return (
        <>
            <h3 className="margin-top margin-bottom">Residential Address</h3>

            {showCountryField && (
                <div className="flex-row">
                    <div className={classes("form-field large-text-input", errors.residentialAddress?.country && "form-field--error")}>
                        <label htmlFor="residential-country" className="text">
                            Country<span className='required'>*</span>
                        </label>
                        <div className="select-box">
                            <select
                                id="residential-country"
                                {...register("residentialAddress.country")}
                            >
                                <option value="">Please Select</option>
                                {countryOptions.map(option => (
                                    <option value={option.value} key={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        {errors.residentialAddress?.country &&
                            <span className="form-message">{errors.residentialAddress.country.message}</span>
                        }
                    </div>
                </div>
            )}

            <AddressFields
                fieldNames={{
                    line1: "residentialAddress.streetAddressLineOne",
                    line2: "residentialAddress.streetAddressLineTwo",
                    city: "residentialAddress.city",
                    state: "residentialAddress.state",
                    zipCode: "residentialAddress.zipCode"
                }}
                labels={AddressLabelsWithRequiredIndicator}
                country={selectedCountry}
            />

            {(watchResidentialAddressState === "FL") &&
                <MultiSelectBox
                    control={control}
                    name="residentialAddress.county"
                    label={<>
                        Residential County{/*
                        */}<span className='required'>*</span>
                    </>}
                    options={FloridaCountyOptions}
                    maximumSelectionLength={1}
                />
            }

            <RadioButtonGroup
                control={control}
                name="businessInFlorida"
                legend={<>
                    Will you be conducting business physically in Florida?{/*
                    */}<span className='required'>*</span>
                </>}
                layout="Two Column"
                options={YesNoOptions}
            />

            {(watchBusinessInFlorida === "true") &&
                <MultiSelectBox
                    control={control}
                    name="businessInFloridaCounty"
                    label={<>
                        FL County{/*
                        */}<span className='required'>*</span>
                    </>}
                    options={FloridaCountyOptions}
                />
            }
        </>
    );
}